import {forwardDispatch} from '@malt/common';
import {clickOutside} from '@malt/dom';
import {screenXs} from '../utils/medias';

Hop.defineModule('c-dropdown', (root) => {
    const $selectEl = root.querySelector('select');
    const $optionsEl = $selectEl.querySelectorAll('option:not(:disabled)');
    const labelText = root.getAttribute('data-label') || '';

    const isMultiple = $selectEl.multiple;
    const isDisabled = $selectEl.disabled;
    const isRequired = $selectEl.required;

    const classDropdown = 'c-dropdown';
    const classDropdownMultiple = classDropdown + '_multiple';
    const classDropdownInput = classDropdown + '__input';
    const classDropdownOpen = classDropdown + '_open';
    const classDropdownFilled = classDropdown + '_filled';
    const classList = 'm-list';
    const classListItem = classList + '__item';
    const datasetListItemSelected = 'data-selected';

    let dropdownEl;
    let dropdownInputEl;
    let dropdownListEl;

    const INIT_DROPDOWN = false;

    function init() {
        initializeState();

        if (isDisabled) {
            return;
        }

        // Don't init Dropdown on Mobile (regular select has better UX)
        if (INIT_DROPDOWN && !screenXs()) {
            initDropdown();

            if (isRequired) {
                isFieldRequired();
            }
        }

        initContextListeners();
    }

    function initializeState() {
        setSelectActive();
    }

    function initDropdown() {
        hideSelect();
        root.appendChild(createDropdown($optionsEl, labelText, isMultiple));
        updateDropdownInput();
        addDropdownListeners();
        propagateEventFromDropdownToSelect();
    }

    function createDropdown(options, label = null, isMultiple = false) {
        dropdownEl = document.createElement('div');
        dropdownEl.classList.add(classDropdown);
        if (isMultiple) {
            dropdownEl.classList.add(classDropdownMultiple);
        }
        dropdownEl.appendChild(createInput(label));
        dropdownEl.appendChild(createList(options));
        return dropdownEl;
    }

    function createInput(label = null) {
        dropdownInputEl = document.createElement('div');
        dropdownInputEl.classList.add(classDropdownInput);
        if (label) {
            dropdownInputEl.textContent = label;
        }
        return dropdownInputEl;
    }

    function createList(options) {
        dropdownListEl = document.createElement('ul');
        dropdownListEl.classList.add(classList);
        options.forEach((option) => {
            const listItem = createListItem(option.value, option.text, option.hasAttribute('selected'));
            dropdownListEl.appendChild(listItem);
        });

        return dropdownListEl;
    }

    function createListItem(value, text, checked = false) {
        const listItemEl = document.createElement('li');
        listItemEl.textContent = text;
        listItemEl.dataset.value = value;
        listItemEl.classList.add(classListItem);
        if (checked) {
            listItemEl.dataset.selected = 'true';
        }
        return listItemEl;
    }

    function hideList() {
        root.classList.remove(classDropdownOpen);
    }

    function toggleList() {
        root.classList.toggle(classDropdownOpen);
    }

    function addSelected(element) {
        element.setAttribute(datasetListItemSelected, true);
    }

    function removeSelected(element) {
        element.removeAttribute(datasetListItemSelected);
        hideList();
        $selectEl.value = null;

        if (isRequired) {
            isFieldRequired();
        }
    }

    function isSelected(element) {
        return element.dataset.selected;
    }

    function updateSelectedOptions() {
        const selectedItems = dropdownListEl.querySelectorAll(`[${datasetListItemSelected}]`);
        const selectedItemsValues = Array.from(selectedItems).map((item) => item.dataset.value);
        const nbOptions = $selectEl.options.length;
        for (let i = 0; i < nbOptions; i++) {
            $selectEl.options[i].selected = selectedItemsValues.indexOf($selectEl.options[i].value) >= 0;
        }
    }

    function updateDropdownItemsSelected() {
        const selectedOptions = Array.from($selectEl.selectedOptions);
        const selectedOptionsValues = selectedOptions.map((option) => option.value);
        const nbItems = dropdownListEl.childNodes.length;
        for (let i = 0; i < nbItems; i++) {
            const item = dropdownListEl.childNodes[i];
            if (selectedOptionsValues.indexOf(item.dataset.value) >= 0) {
                item.setAttribute(datasetListItemSelected, true);
            } else {
                item.removeAttribute(datasetListItemSelected);
            }
        }
    }

    function updateDropdown() {
        updateDropdownItemsSelected();
        updateDropdownInput();
    }

    function updateDropdownInput() {
        const selectedItems = dropdownListEl.querySelectorAll(`[${datasetListItemSelected}]`);
        const selectedItemsTextContents = Array.from(selectedItems).map((item) => item.textContent);
        const selectedItemsValues = Array.from(selectedItems).map((item) => item.dataset.value);
        if (selectedItemsTextContents.length > 0) {
            dropdownInputEl.textContent = Array.from(selectedItemsTextContents).join(', ');
            dropdownInputEl.dataset.value = Array.from(selectedItemsValues).join(', ');
            dropdownEl.classList.add(classDropdownFilled);
        } else {
            dropdownInputEl.textContent = labelText;
            dropdownEl.classList.remove(classDropdownFilled);
        }
    }

    function handleRoot(event) {
        if (event.target) {
            // Click on Input Dropdown: Show/Hide List
            // If the field is required, we add a child node to classDropdownInput (a <p> node to handle "*")
            // So most of the time we'll click on this <p>, then we need to check the parent
            if (event.target.classList.contains(classDropdownInput) || event.target.parentNode.classList.contains(classDropdownInput)) {
                toggleList();
            }
            // Click on List Item Dropdown: handle selected Items
            if (event.target.classList.contains(classListItem)) {
                handleListItem(event);
            }
            // Click on Select
            if (event.target.tagName === 'select') {
                updateDropdown();
            }
        }
    }

    function handleListItem(event) {
        if (isSelected(event.target)) {
            removeSelected(event.target);
        } else {
            const selectedOption = dropdownListEl.querySelector(`[${datasetListItemSelected}]`);
            if (!isMultiple) {
                if (selectedOption) {
                    removeSelected(selectedOption);
                }
                hideList();
            }
            addSelected(event.target, isMultiple);
        }
        updateSelectedOptions();
        updateDropdownInput();
        dropdownEl.dispatchEvent(new Event('change'));
    }

    function closeIfEventOutside(event) {
        if (clickOutside(event, root)) {
            hideList();
        }
    }

    // function removeDropdown() {
    //     dropdownEl.parentNode.removeChild(dropdownEl);
    //     dropdownEl = null;
    //     removeDropdownListeners();
    // }

    // function showSelect() {
    //     $selectEl.hidden = false;
    // }

    function hideSelect() {
        $selectEl.hidden = true;
    }

    // function showSelectIfMobile() {
    //     if (screenXs()) {
    //         if (dropdownEl) {
    //             removeDropdown();
    //             showSelect();
    //         }
    //     } else {
    //         if (!dropdownEl) {
    //             initDropdown();
    //             if (isRequired) {
    //                 isFieldRequired();
    //             }
    //         }
    //     }
    // }

    function isFieldRequired() {
        const fakeLabel = root.querySelector(`.${classDropdownInput}`);
        fakeLabel.classList.add(`${classDropdownInput}-required`);
        fakeLabel.innerHTML = `<p>${labelText}<span>*</span></p>`;
    }

    function setSelectActive() {
        if ($selectEl.value || $selectEl.value !== '') {
            $selectEl.classList.add('active');
        }
    }

    // const debouncedShowSelectIfMobile = debounce(showSelectIfMobile, 100);

    function initContextListeners() {
        document.addEventListener('click', closeIfEventOutside);
        $selectEl.addEventListener('change', setSelectActive);
        // window.addEventListener('resize', debouncedShowSelectIfMobile);
    }

    function addDropdownListeners() {
        root.addEventListener('click', handleRoot);
    }

    function removeDropdownListeners() {
        root.removeEventListener('click', handleRoot);
    }

    function propagateEventToSelect(event) {
        return forwardDispatch(event, $selectEl);
    }

    function propagateEventFromDropdownToSelect() {
        dropdownEl.addEventListener('click', propagateEventToSelect);
        dropdownEl.addEventListener('focus', propagateEventToSelect);
        dropdownEl.addEventListener('change', propagateEventToSelect);
        dropdownEl.addEventListener('blur', propagateEventToSelect);
    }

    function destroyPropagateEventFromDropdownToSelect() {
        dropdownEl.removeEventListener('click', propagateEventToSelect);
        dropdownEl.removeEventListener('focus', propagateEventToSelect);
        dropdownEl.removeEventListener('change', propagateEventToSelect);
        dropdownEl.removeEventListener('blur', propagateEventToSelect);
    }

    init();

    return function destroy() {
        document.removeEventListener('click', closeIfEventOutside);
        $selectEl.removeEventListener('change', setSelectActive);

        if (INIT_DROPDOWN) {
            // window.removeEventListener('resize', showSelectIfMobile);
            removeDropdownListeners();
            destroyPropagateEventFromDropdownToSelect();
        }
    };
});
